import {
  signIn,
  signOut,
  fetchAuthSession,
  resetPassword,
  updatePassword,
  confirmResetPassword,
  ResetPasswordOutput
} from 'aws-amplify/auth'
import { toast } from 'vue-sonner'
import router from '@/router'
import { type User } from '@/types'
import { normalizeRoles } from './roleNormalizer'
import { storeRegistry } from '@/store/storeRegistry'
import { nextTick } from 'vue'


// Enum for Status Codes
enum StatusCode {
  Success = 200,
  NoContent = 204,
  Unauthorized = 401,
  NotFound = 404,
  Conflict = 409,
  CustomError = 420,
  InternalServerError = 500
}

// Cached Token (Prevents multiple calls)
let cachedIdToken: any = null
let expiry: number | undefined = 0
let tokenTimestamp = 0

async function getLatestIdToken() {
  const currentTime = Date.now()

  // console.debug(`token expiry: ${expiry}`)

  if (expiry) {
    const expiryTimeMs = expiry * 1000; // Convert seconds to milliseconds.
    const timeLeftMs = expiryTimeMs - currentTime;
    const timeLeftSeconds = timeLeftMs / 1000;

    if (timeLeftSeconds > 60 && cachedIdToken && (currentTime - tokenTimestamp < 300000)) {
      return cachedIdToken
    }
  }

  try {
    const { tokens } = await fetchAuthSession()
    cachedIdToken = tokens?.idToken ?? null
    expiry = tokens?.idToken?.payload?.exp
    tokenTimestamp = Date.now()
    return cachedIdToken
  } catch (err) {
    console.error('Failed to fetch the latest idToken:', err)
    return null
  }
}

/**
 * Returns the first non-null/undefined value from the payload for the given claim keys.
 * Falls back to defaultValue if none match.
 */
function getClaimValue(payload: Record<string, unknown>, keys: string[], defaultValue: string = ''): string {
  for (const key of keys) {
    const value = payload[key]
    if (value != null) return String(value)
  }
  return defaultValue
}

export async function getUserFromToken() {
  const { tokens } = await fetchAuthSession()
  if (!tokens?.idToken) return null

  const payload = tokens.idToken.payload

  return {
    id: getClaimValue(payload, ["custom:aplos_user_id", "custom:user_id"]),
    email: payload.email ?? '',
    first_name: payload.given_name ?? '',
    last_name: payload.family_name ?? '',
    tenant_id: getClaimValue(payload, ["custom:aplos_user_tenant_id", "custom:tenant_id"]),
    roles: (() => {
      const rolesValue = payload["custom:aplos_user_roles"] ?? payload["custom:user_roles"]
      if (rolesValue == null) return []
      return normalizeRoles(String(rolesValue).split(","))
    })()
  } as User
}

export async function fetchAndUpdateUser() {
  await fetchAuthSession()
}

export async function logout(displayMessage: boolean = true, routeToRoot: boolean = true) {
  try {
    await signOut();
    resetUser();
    if (routeToRoot) {
      await nextTick();
      await router.push('/');
    }



    // resetUser();
    if (displayMessage) {
      toast.success('You have been logged out.');
    }

  } catch (error) {
    console.error('Error during logout:', error);
    toast.error('Failed to log out.');
  }
}

function resetUser() {
  try {
    cachedIdToken = null
    expiry = 0
    tokenTimestamp = 0

    storeRegistry.resetAll()
  } catch (error) {
    console.error('Error resetting user:', error)
  }
}

// Login Function
export async function login(username: string | undefined, password: string | undefined) {
  try {
    if (!username || !password) {
      const message = !username ? 'Username is required' : 'Password is required'
      return { statusCode: StatusCode.CustomError, response_message: message }
    }
    // use store the usernames in lower case.  
    // some cognito userpools (most at this time) are case sensitive
    username = username.toLowerCase()

    const { isSignedIn, nextStep } = await signIn({ username, password })

    return { statusCode: StatusCode.Success, isSignedIn, nextStep, response_message: 'Success' }

  } catch (error: any) {
    let error_message = 'An error occurred during login'
    let error_code = StatusCode.InternalServerError

    if (error?.name === 'UserAlreadyAuthenticatedException') {
      await logout()
      return { statusCode: StatusCode.Conflict, response_message: 'User already authenticated. Please log in again.' }
    }
    if (error?.name === 'UserNotFoundException' || error?.name === 'NotAuthorizedException') {
      error_message = 'Invalid email or password'
      error_code = StatusCode.NotFound
    } else {
      console.error('Login error:', error)
    }

    return { statusCode: error_code, response_message: error_message }
  }
}

// Check Login Status
export async function isLoggedIn() {
  try {
    const idToken = await getLatestIdToken()
    return idToken !== null && !isTokenExpired(idToken)
  } catch (error) {
    console.error('Error checking login status:', error)
    return false
  }
}

// Token Expiration Check
function isTokenExpired(token: any): boolean {
  try {
    if (!token || !token.payload) return true

    const currentTime = Date.now() / 1000
    if (token?.payload?.exp === undefined) {
      console.warn('Token has no expiration setting. Treating as expired.')
      return true
    }
    return token.payload.exp < currentTime
  } catch (error) {
    console.error("Failed to decode token. Treating as expired", error)
    return true
  }
}

// Get JWT Token
export async function get_jwt_token() {
  return await getLatestIdToken()
}

// Send Password Reset Code
export async function sendUserVerificationCode(username: string) {

  username = username.toLowerCase()

  const output = await resetPassword({ username })
  return handleResetPasswordNextSteps(output)
}

// Reset Password with Code
export async function resetUserPasswordWithVerificationCode(
  username: string,
  confirmationCode: string,
  newPassword: string
) {
  try {
    username = username.toLowerCase()

    await confirmResetPassword({ username, confirmationCode, newPassword })
    return { statusCode: StatusCode.Success, response_message: 'Password Updated' }
  } catch (err) {
    console.error(err)
    return { statusCode: StatusCode.InternalServerError, response_message: `Password Update Failed. Reason: ${err}` }
  }
}

// Update Signed-in User's Password
export async function updateSignedInUserPassword(oldPassword: string, newPassword: string) {
  try {
    await updatePassword({ oldPassword, newPassword })
    return { statusCode: StatusCode.Success, response_message: 'Password Updated' }
  } catch (err) {
    console.error(err)
    return { statusCode: StatusCode.InternalServerError, response_message: `Password Update Failed. Reason: ${err}` }
  }
}

// Handle Reset Password Steps
export async function handleResetPasswordNextSteps(output: ResetPasswordOutput) {
  const { nextStep } = output
  let response_message = ''
  let statusCode = StatusCode.Success
  const codeDeliveryDetails = nextStep.codeDeliveryDetails

  switch (nextStep.resetPasswordStep) {
    case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
      statusCode = StatusCode.NoContent
      response_message = `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`

      break
    case 'DONE':
      response_message = 'Successfully reset password.'

      break
    default:
      statusCode = StatusCode.NotFound
      response_message = "We can't validate your user account.  Usernames and email addresses are case-sensitive. Please try again with all lowercase or contact support if this continues."
      console.error(`Reset password error output: ${output}`)
      break
  }

  return { statusCode, response_message }
}

export { validateTokenClaims, type TokenValidationResult } from './tokenValidator'
